import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Components
import { ExportOrdersDropdown } from './ExportOrdersDropdown';
import { Button } from '@raptormaps/button';

// Hooks
import useDebounce from '../../hooks/useDebounce';
import { Row } from '@raptormaps/layout';
import { DropdownMenu, type DropdownItem } from '@raptormaps/dropdown-menu';
import { GroupedButton, GroupedInput } from './styles/OrderStatus.styles';

const searchFieldOpions: { [key: string]: { [key: string]: string } } = {
  id: {
    id: 'id',
    title: 'Tracking Number',
  },
  solar_farm_name: {
    id: 'solar_farm_name',
    title: 'Farm Name',
  },
  client_org_name: {
    id: 'client_org_name',
    title: 'Client Org',
  },
};

interface OrderTableSearchProps {
  orderActions: any;
}

/**
 * Contains a search bar, advanced search options and export options for the order table
 * @param {*} orderActions - suite of order redux actions
 */
export const OrderTableSearch = ({ orderActions }: OrderTableSearchProps) => {
  const defaultTextSearchValues = {
    display: searchFieldOpions.solar_farm_name.title,
    field: searchFieldOpions.solar_farm_name.id,
  };
  const [searchField, setSearchField] = useState(defaultTextSearchValues);
  const [searchText, setSearchText] = useState('');

  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const filterType = params.get('type');
  const filterText = params.get('value');

  // Hooks
  const searchTextDebounced = useDebounce(searchText, 1000);

  const handleAddQueryParams = (type: string, value: string) => {
    const newParams = `?type=${type}&value=${value}`;
    history.push({ search: newParams.toString() });
  };

  useEffect(() => {
    if (searchTextDebounced && searchText) {
      handleAddQueryParams(searchField.field, searchTextDebounced);
    }
    if (searchTextDebounced === '') {
      history.push({ search: '' });
    }
  }, [searchTextDebounced, searchField, searchText]);

  useEffect(() => {
    if (filterType !== null && filterText !== null) {
      setSearchField({
        display: searchFieldOpions[filterType].title,
        field: searchFieldOpions[filterType].id,
      });
      setSearchText(filterText);
    }
  }, [filterText, filterType]);

  const handleClearFilter = () => {
    setSearchText('');
    params.delete('type');
    params.delete('value');
    history.push({ search: params.toString() });

    setSearchField(defaultTextSearchValues);
  };

  const filterOptions: DropdownItem[] = [
    {
      variant: 'option',
      value: searchFieldOpions.id.title,
      onSelect: () => {
        setSearchField({
          display: searchFieldOpions.id.title,
          field: searchFieldOpions.id.id,
        });
        setSearchText('');
      },
    },
    {
      variant: 'option',
      value: searchFieldOpions.solar_farm_name.title,
      onSelect: () => {
        setSearchField({
          display: searchFieldOpions.solar_farm_name.title,
          field: searchFieldOpions.solar_farm_name.id,
        });
        setSearchText('');
      },
    },
    {
      variant: 'option',
      value: searchFieldOpions.client_org_name.title,
      onSelect: () => {
        setSearchField({
          display: searchFieldOpions.client_org_name.title,
          field: searchFieldOpions.client_org_name.id,
        });
        setSearchText('');
      },
    },
  ];

  return (
    <Row align="center">
      <Row gutter="none">
        <GroupedInput
          id="order-search"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <DropdownMenu items={filterOptions}>
          <GroupedButton variant="secondary" style={{}}>
            {searchField.display}
          </GroupedButton>
        </DropdownMenu>
      </Row>
      <Button onClick={handleClearFilter}>Clear Filter</Button>
      <ExportOrdersDropdown
        exportSolarOrders={orderActions.exportSolarOrders}
      />
    </Row>
  );
};
